import type { BowtieConfiguration } from '../@types/common-data-types';
import type { TransformedSimpleRecordDto } from '../api/@types/enhanced-v4-api.types';
import { enhancedV4Api } from '../api/enhanced/enhanced-v4-api';
import type { AppDispatch } from '../redux/store';

/**
 * A snapshot of the diagram state taken at "Create Backend Records" click time.
 * Captured by the hook so it survives the redux state being reset during navigation.
 */
export type DiagramSnapshot = {
  causes: ReadonlyArray<{
    id: string;
    label?: string;
    controls: ReadonlyArray<{ id: string; label?: string }>;
  }>;
  consequences: ReadonlyArray<{
    id: string;
    label?: string;
    controls: ReadonlyArray<{ id: string; label?: string }>;
  }>;
  escalationFactors: ReadonlyArray<{
    id: string;
    label?: string;
    controlNodeId?: string;
    barriers?: ReadonlyArray<{ id: string; label?: string }>;
  }>;
};

export type EscalationFactorPersistenceResult = {
  /** Total EFs we attempted (excludes EFs without a label or controlNodeId). */
  total: number;
  /** EFs fully created and visible: record exists, barriers all created, MUE reverse-link patched. */
  created: number;
  /** EFs that exist as records but have an issue (some barriers failed, EF→barriers patch failed, or MUE→EFs patch failed). */
  partial: number;
  /** EFs whose parent control could not be matched against a created backend record. */
  skipped: number;
  /** EFs whose record creation itself failed. */
  failed: number;
};

/**
 * After the v4 "create-bowtie-records" background job has produced the MUE + cause +
 * consequence + control records, follow up from the FE to create the escalation factor and
 * barrier records (the v4 BowtieDTO doesn't carry them).
 *
 * Compound key is (kind, parentLabel, controlLabel, indexUnderParent): the kind prefix keeps
 * cause-side and consequence-side labels apart, and the trailing index disambiguates duplicate
 * control labels (the backend service merges parents that share a label, so cumulative indexing
 * on the snapshot side aligns with the backend's submission-order concatenation).
 */
export const createEscalationFactorsAfterJob = async (
  dispatch: AppDispatch,
  mueRecordId: number,
  bowtieConfiguration: BowtieConfiguration,
  snapshot: DiagramSnapshot
): Promise<EscalationFactorPersistenceResult> => {
  const result: EscalationFactorPersistenceResult = { total: 0, created: 0, partial: 0, skipped: 0, failed: 0 };

  // Diagram EFs with the data required to link them. Anything missing label or controlNodeId
  // can't be persisted; skip silently (the user removed the parent control or never labeled the EF).
  const candidateEfs = snapshot.escalationFactors.filter((ef) => ef.label && ef.controlNodeId) as ReadonlyArray<
    DiagramSnapshot['escalationFactors'][number] & { label: string; controlNodeId: string }
  >;

  if (candidateEfs.length === 0) return result;

  result.total = candidateEfs.length;

  const efConfig = bowtieConfiguration.escalationFactors;
  const efbConfig = bowtieConfiguration.escalationFactorBarriers;
  const mueEfReverseLinkFieldId = bowtieConfiguration.main.escalationFactorsReverseRecordLinkFieldId;

  // Tenant doesn't have EF/EFB forms configured but the draft still contains EFs (the UI permits
  // adding them to non-record-backed diagrams). Nothing we can do — surface as failures so the
  // success toast tells the user the EFs were not saved.
  if (!efConfig || !efbConfig || !mueEfReverseLinkFieldId) {
    result.failed = candidateEfs.length;
    return result;
  }

  const controlNodeIdToCompoundKey = buildSnapshotControlKeys(snapshot);

  // Fetch MUE record to get cause + consequence record id arrays via the reverse-link fields.
  const mueRecord = await dispatch(
    enhancedV4Api.endpoints.getSimpleRecord.initiate({ id: mueRecordId, params: {} }, { forceRefetch: true })
  ).unwrap();

  if (!mueRecord) throw new Error('Could not fetch MUE record to link escalation factors');

  const causeRecordIds = toNumberArray(mueRecord.fields?.[bowtieConfiguration.main.causesReverseRecordLinkFieldId]);
  const consequenceRecordIds = toNumberArray(
    mueRecord.fields?.[bowtieConfiguration.main.consequencesReverseRecordLinkFieldId]
  );

  // Fetch the cause + consequence records in parallel.
  const [causeRecords, consequenceRecords] = await Promise.all([
    causeRecordIds.length > 0
      ? dispatch(
          enhancedV4Api.endpoints.getSimpleRecords1.initiate({
            formId: bowtieConfiguration.causes.formId,
            filter: `id:in:${causeRecordIds.join(',')}`,
            pageSize: 200,
          })
        ).unwrap()
      : Promise.resolve([] as TransformedSimpleRecordDto[]),
    consequenceRecordIds.length > 0
      ? dispatch(
          enhancedV4Api.endpoints.getSimpleRecords1.initiate({
            formId: bowtieConfiguration.consequences.formId,
            filter: `id:in:${consequenceRecordIds.join(',')}`,
            pageSize: 200,
          })
        ).unwrap()
      : Promise.resolve([] as TransformedSimpleRecordDto[]),
  ]);

  // Collect every control record id linked from the causes + consequences, then batch-fetch them.
  const allControlIds = collectControlIds(
    causeRecords,
    consequenceRecords,
    bowtieConfiguration.causes.controlsReverseRecordLinkFieldId,
    bowtieConfiguration.consequences.controlsReverseRecordLinkFieldId
  );
  const controlRecords =
    allControlIds.length > 0
      ? await dispatch(
          enhancedV4Api.endpoints.getSimpleRecords1.initiate({
            formId: bowtieConfiguration.controls.formId,
            filter: `id:in:${allControlIds.join(',')}`,
            pageSize: 500,
          })
        ).unwrap()
      : [];

  const compoundKeyToControlRecordId = buildBackendControlKeys(
    causeRecords,
    consequenceRecords,
    controlRecords,
    bowtieConfiguration
  );

  const efStatus = pickDraftOrFirstWorkflowStep(efConfig.form);
  const efbStatus = pickDraftOrFirstWorkflowStep(efbConfig.form);

  // Track every successfully-created EF (recordId + whether it had a barrier-side issue) so we can
  // patch the MUE reverse-link in one pass at the end.
  const createdEfs: Array<{ recordId: number; hadIssue: boolean }> = [];

  for (const ef of candidateEfs) {
    const compoundKeyForEf = controlNodeIdToCompoundKey.get(ef.controlNodeId);
    const controlRecordId = compoundKeyForEf ? compoundKeyToControlRecordId.get(compoundKeyForEf) : undefined;

    if (!controlRecordId) {
      result.skipped += 1;
      continue;
    }

    let newEfRecordId: number | undefined;
    try {
      const efResponse = await dispatch(
        enhancedV4Api.endpoints.createSimpleRecords.initiate({
          params: {},
          body: [
            {
              formId: efConfig.formId,
              status: efStatus,
              fields: {
                [efConfig.fieldId]: ef.label,
                [efConfig.mainFormRecordLinkFieldId]: [String(mueRecordId)],
                [efConfig.controlRecordLinkFieldId]: [String(controlRecordId)],
              },
            },
          ],
        })
      ).unwrap();
      if (efResponse[0]?.success && efResponse[0].id != null) {
        newEfRecordId = efResponse[0].id;
      }
    } catch {
      // EF create threw — treat the EF as not created.
    }

    if (newEfRecordId == null) {
      result.failed += 1;
      continue;
    }

    // Record the EF before the barrier work so the MUE reverse-link patch still covers it if a
    // subsequent step throws.
    let hadIssue = false;
    const expectedBarrierCount = (ef.barriers ?? []).filter((barrier) => Boolean(barrier.label)).length;
    const newBarrierRecordIds: number[] = [];

    // Batch all of this EF's barriers into a single createSimpleRecords call.
    const labelledBarriers = (ef.barriers ?? []).filter((barrier) => Boolean(barrier.label));
    if (labelledBarriers.length > 0) {
      try {
        const barrierResponses = await dispatch(
          enhancedV4Api.endpoints.createSimpleRecords.initiate({
            params: {},
            body: labelledBarriers.map((barrier) => ({
              formId: efbConfig.formId,
              status: efbStatus,
              fields: {
                [efbConfig.fieldId]: barrier.label,
                [efbConfig.mainFormRecordLinkFieldId]: [String(mueRecordId)],
                [efbConfig.controlRecordLinkFieldId]: [String(controlRecordId)],
                [efbConfig.escalationFactorRecordLinkFieldId]: [String(newEfRecordId)],
              },
            })),
          })
        ).unwrap();

        for (const response of barrierResponses) {
          if (response?.success && response.id != null) {
            newBarrierRecordIds.push(response.id);
          } else {
            hadIssue = true;
          }
        }
      } catch {
        hadIssue = true;
      }
    }

    if (newBarrierRecordIds.length < expectedBarrierCount) hadIssue = true;

    // Patch the EF with the reverse-link to its barriers. Mirrors useCloneBowtie's behaviour.
    if (newBarrierRecordIds.length > 0) {
      try {
        await dispatch(
          enhancedV4Api.endpoints.patchSimple.initiate({
            id: newEfRecordId,
            params: {},
            simpleRecordDto: {
              formId: efConfig.formId,
              fields: {
                [efConfig.barriersReverseRecordLinkFieldId]: newBarrierRecordIds,
              },
            },
          })
        ).unwrap();
      } catch {
        hadIssue = true;
      }
    }

    createdEfs.push({ recordId: newEfRecordId, hadIssue });
  }

  // Patch the MUE's reverse-link to point at every new EF. Load-bearing: useRecord discovers
  // EFs via this field, so a patch failure makes them invisible even though they exist on the
  // backend (every created EF then gets demoted to partial). Merge with existing values rather
  // than overwriting, in case a parallel flow has linked some.
  let mueLinkFailed = false;
  if (createdEfs.length > 0) {
    try {
      const existing = (mueRecord.fields?.[mueEfReverseLinkFieldId] as Array<unknown> | undefined) ?? [];
      const existingNumeric = existing
        .map((v) => (typeof v === 'number' ? v : Number(v)))
        .filter((n): n is number => Number.isFinite(n));
      const merged = [...new Set([...existingNumeric, ...createdEfs.map((ef) => ef.recordId)])];
      await dispatch(
        enhancedV4Api.endpoints.patchSimple.initiate({
          id: mueRecordId,
          params: {},
          simpleRecordDto: {
            formId: bowtieConfiguration.main.formId,
            fields: {
              [mueEfReverseLinkFieldId]: merged,
            },
          },
        })
      ).unwrap();
    } catch {
      mueLinkFailed = true;
    }
  }

  for (const createdEf of createdEfs) {
    if (mueLinkFailed || createdEf.hadIssue) {
      result.partial += 1;
    } else {
      result.created += 1;
    }
  }

  return result;
};

const buildSnapshotControlKeys = (snapshot: DiagramSnapshot): Map<string, string> => {
  const map = new Map<string, string>();
  // Index by (kind, parentLabel, controlLabel) globally rather than per-parent, because the
  // backend's BackgroundJobsService.createBowtie merges every parent that shares a label into
  // a single record, concatenating their controls in submission order. Cumulative indexing on
  // the snapshot side matches that merge ordering — when parent labels are unique within a
  // bowtie (the common case) it degenerates to per-parent indexing.
  const indexes = new Map<string, number>();
  const addParent = (kind: ParentKind, parent: DiagramSnapshot['causes'][number]) => {
    if (!parent.label) return;
    for (const control of parent.controls) {
      if (!control.label) continue;
      const indexKey = `${kind}\0${parent.label}\0${control.label}`;
      const idx = indexes.get(indexKey) ?? 0;
      map.set(control.id, compoundKey(kind, parent.label, control.label, idx));
      indexes.set(indexKey, idx + 1);
    }
  };
  for (const cause of snapshot.causes) addParent('cause', cause);
  for (const consequence of snapshot.consequences) addParent('consequence', consequence);
  return map;
};

const collectControlIds = (
  causeRecords: TransformedSimpleRecordDto[],
  consequenceRecords: TransformedSimpleRecordDto[],
  causesControlsLinkFieldId: number,
  consequencesControlsLinkFieldId: number
): number[] => {
  const ids = new Set<number>();
  for (const r of causeRecords) {
    for (const id of toNumberArray(r.fields?.[causesControlsLinkFieldId])) ids.add(id);
  }
  for (const r of consequenceRecords) {
    for (const id of toNumberArray(r.fields?.[consequencesControlsLinkFieldId])) ids.add(id);
  }
  return [...ids];
};

const buildBackendControlKeys = (
  causeRecords: TransformedSimpleRecordDto[],
  consequenceRecords: TransformedSimpleRecordDto[],
  controlRecords: TransformedSimpleRecordDto[],
  bowtieConfiguration: BowtieConfiguration
): Map<string, number> => {
  const controlIdToRecord = new Map<number, TransformedSimpleRecordDto>();
  for (const ctrl of controlRecords) {
    if (ctrl.id != null) controlIdToRecord.set(ctrl.id, ctrl);
  }
  const map = new Map<string, number>();
  const addParent = (
    kind: ParentKind,
    parentRecord: TransformedSimpleRecordDto,
    parentLabelFieldId: number,
    controlsLinkFieldId: number
  ) => {
    const parentLabel = String(parentRecord.fields?.[parentLabelFieldId] ?? '');
    if (!parentLabel) return;
    const labelIndexes = new Map<string, number>();
    for (const controlId of toNumberArray(parentRecord.fields?.[controlsLinkFieldId])) {
      const controlRecord = controlIdToRecord.get(controlId);
      if (!controlRecord) continue;
      const controlLabel = String(controlRecord.fields?.[bowtieConfiguration.controls.fieldId] ?? '');
      if (!controlLabel) continue;
      const idx = labelIndexes.get(controlLabel) ?? 0;
      map.set(compoundKey(kind, parentLabel, controlLabel, idx), controlId);
      labelIndexes.set(controlLabel, idx + 1);
    }
  };
  for (const r of causeRecords) {
    addParent(
      'cause',
      r,
      bowtieConfiguration.causes.fieldId,
      bowtieConfiguration.causes.controlsReverseRecordLinkFieldId
    );
  }
  for (const r of consequenceRecords) {
    addParent(
      'consequence',
      r,
      bowtieConfiguration.consequences.fieldId,
      bowtieConfiguration.consequences.controlsReverseRecordLinkFieldId
    );
  }
  return map;
};

type ParentKind = 'cause' | 'consequence';

// NUL-separated so labels containing the delimiter can't collide.
const compoundKey = (kind: ParentKind, parentLabel: string, controlLabel: string, indexUnderParent: number) =>
  `${kind}\0${parentLabel}\0${controlLabel}\0${indexUnderParent}`;

const toNumberArray = (value: unknown): number[] => {
  if (!Array.isArray(value)) return [];
  const result: number[] = [];
  for (const item of value) {
    const n = typeof item === 'number' ? item : Number(item);
    if (Number.isFinite(n)) result.push(n);
  }
  return result;
};

const pickDraftOrFirstWorkflowStep = (form: BowtieConfiguration['main']['form']): string | undefined => {
  const steps = form.workflowSteps ?? [];
  return steps.find((step) => step.draft === true)?.label ?? steps[0]?.label;
};
